/**
 * BNO dry/wet audio player.
 *
 * One implementation behind both preview surfaces: the plugin PDP "audio previews"
 * section and the bundle PDP Learn-more modal. Each player plays a dry (bypass) and a
 * wet (processed) file in lockstep on one shared Web Audio graph, so the dry/wet toggle
 * is a real simultaneous gain crossfade: you always hear exactly one, the playhead never
 * jumps, and there is no click.
 *
 * Both surfaces render the same bar (snippets/bno-audio-player-bar.liquid) off the same
 * styling (the DRY/WET AUDIO PLAYER block in assets/bno-shared.css), so this module
 * enhances one markup shape and self-registers at the bottom of the file. A section only
 * has to load it.
 *
 * Design notes:
 * - One shared AudioContext, created and resumed inside the first play gesture (autoplay
 *   policy). Nothing is fetched or decoded on page load: a player fetches and decodes its
 *   two files on ITS first play, since one plugin's demos run to many MB of WAV.
 * - Decoded buffers are cached by URL, so re-open and re-play never refetch.
 * - Dry and wet both run (loop=true, loopEnd clamped to the shorter of the two) started at
 *   the same context time and offset, so they stay phase-locked. Gains pick which is heard.
 * - Playback loops. That is deliberate on both surfaces: you want the loop running while
 *   you flip dry/wet, which is the whole point of the comparison. The seam is declicked
 *   in the samples at decode (see declickEdges), because the source files are hard cuts
 *   and the jump from their last sample back to their first is a step.
 * - Single playback across the page: a module-level registry stops the player that was
 *   sounding before a new one starts. Foreign players (any other <audio> on the page) are
 *   paused on start, and a `bno:audio-play` event is dispatched and listened for, so a
 *   surface that does not use this module still cannot double up with one that does.
 * - Stop on close: a player inside a <dialog> stops on that dialog's native `close` event
 *   (fired by the close button, Esc and click-outside alike, via dialog.close()).
 * - The stylized waveform is seeded deterministically from the dry filename: stable
 *   across reloads, different per preview, no Math.random() at render.
 * - prefers-reduced-motion: progress still tracks playback but steps coarsely instead of
 *   sweeping every frame. Audio is unaffected.
 */

const WAVE_BARS = 96; // stylized waveform bars: dense enough that the thin, rounded bars
// read as a filled waveform rather than spaced ticks, across a wide desktop bar
const CROSSFADE = 0.08; // dry/wet crossfade time, seconds
// Loop seam declick, milliseconds per edge (so 2x this is the total dip across the
// seam). 3ms kills the step on the current files while staying far too short to hear
// as a level dip: it is 0.03% of a 20 second loop. Below about 1ms the fade stops
// band-limiting low frequency content and can thump; above about 10ms it starts to be
// audible as a gap on continuous material.
const DECLICK_MS = 3;
const PLAY_EVENT = 'bno:audio-play';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

// The one markup shape, from snippets/bno-audio-player-bar.liquid.
const ROOT = '[data-bno-player]';
const PLAY = '.bno-player__play';
const WAVE = '.bno-player__wave';
const TOGGLE = '.bno-player__toggle';
const LAYER = 'bno-player__wave-layer';

/** @type {AudioContext | null} */
let audioCtx = null;
/** @type {Map<string, Promise<AudioBuffer>>} */
const bufferCache = new Map();
/** @type {DryWetPlayer | null} the player currently sounding (single-playback registry) */
let currentPlayer = null;
/** Set while this module dispatches PLAY_EVENT, so its own listener ignores the echo. */
let announcing = false;

/** Lazily create the one shared context (called inside a user gesture). */
function getContext() {
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    audioCtx = new Ctx();
  }
  return audioCtx;
}

/**
 * Declick the loop seam, in the samples themselves.
 *
 * The preview files are hard cuts of continuous material: they start and end mid
 * waveform, so the jump from the last sample back to the first is a step, and a step
 * is a click. Measured on the current pair, that jump is the single largest
 * sample-to-sample transition anywhere in the 20 second file, about twice the next
 * largest, so it is audible.
 *
 * Fix it by taking each buffer to zero across DECLICK_MS at both edges, once, right
 * after decode. That makes the seam silence-to-silence, which is sample accurate by
 * construction: it IS the samples, so there is no automation to schedule, nothing to
 * top up for an endless loop, and nothing that can drift after a pause or a seek. A
 * raised cosine (not linear) so the envelope has no corner of its own to tick on.
 *
 * This MASKS a defect in the files rather than fixing it. A file cut on a zero
 * crossing, or cut to a musical bar so the tail leads back into the head, would not
 * need this and would also loop musically instead of merely quietly.
 */
function declickEdges(buffer) {
  const fade = Math.min(
    Math.round((buffer.sampleRate * DECLICK_MS) / 1000),
    Math.floor(buffer.length / 2)
  );
  if (fade <= 0) return buffer;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const s = buffer.getChannelData(c);
    for (let i = 0; i < fade; i++) {
      const g = 0.5 * (1 - Math.cos((Math.PI * i) / fade)); // 0 -> 1, flat at both ends
      s[i] *= g;
      s[buffer.length - 1 - i] *= g;
    }
  }
  return buffer;
}

/** Fetch and decode a file once, cached by URL. Decoded buffers are declicked. */
function loadBuffer(url) {
  let pending = bufferCache.get(url);
  if (!pending) {
    const ctx = getContext();
    pending = fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`fetch ${url} -> ${res.status}`);
        return res.arrayBuffer();
      })
      .then((data) => ctx.decodeAudioData(data))
      // Once per URL, before anything can play it: the cache holds the treated buffer.
      .then(declickEdges);
    // Cache the promise so concurrent and repeat requests share one fetch and decode.
    bufferCache.set(url, pending);
    // Drop a rejected entry so a later play can retry.
    pending.catch(() => bufferCache.delete(url));
  }
  return pending;
}

/**
 * Deterministic PRNG seeded from a string (xmur3 then mulberry32), so the waveform is
 * stable across reloads yet unique per preview.
 */
function seededRandom(seed) {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = (h ^= h >>> 16) >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A stable seed for a player: the dry filename (falls back to wet, then any text). */
function waveSeed(dryUrl, wetUrl) {
  const src = dryUrl || wetUrl || 'bno-demo';
  try {
    return new URL(src, window.location.href).pathname; // filename path, no ?v= cache-buster
  } catch (e) {
    return src;
  }
}

class DryWetPlayer {
  /** @param {HTMLElement} el the player root, carrying data-dry / data-wet */
  constructor(el) {
    this.el = el;
    this.dryUrl = el.dataset.dry || '';
    this.wetUrl = el.dataset.wet || '';

    this.playBtn = el.querySelector(PLAY);
    this.wave = el.querySelector(WAVE);
    this.toggle = el.querySelector(TOGGLE);

    // Both accessible names come from the one label the section rendered, so they can
    // never drift apart: "Play <name>" becomes "Pause <name>".
    this.playLabel = this.playBtn.getAttribute('aria-label') || 'Play';
    this.pauseLabel = this.playLabel.replace(/^Play\b/, 'Pause');

    /** @type {{dry: AudioBuffer|null, wet: AudioBuffer|null} | null} */
    this.buffers = null;
    this.loading = null;

    this.playing = false;
    this.wet = false; // default off = dry
    this.loopLen = 0;
    this.offset = 0; // playhead position within the loop, seconds
    this.startedAt = 0; // ctx.currentTime when the current sources started
    this.startOffset = 0; // offset the current sources started from

    this.drySource = null;
    this.wetSource = null;
    this.dryGain = null;
    this.wetGain = null;
    this.raf = 0;

    this.#buildWave();
    this.#paintProgress(0);
    this.#bind();
  }

  #buildWave() {
    const rand = seededRandom(waveSeed(this.dryUrl, this.wetUrl));
    const bars = [];
    for (let i = 0; i < WAVE_BARS; i++) {
      // Height 18%..100%, gently shaped so it reads as a waveform, not noise.
      const env = 0.55 + 0.45 * Math.sin((i / WAVE_BARS) * Math.PI); // fuller in the middle
      const h = Math.round((0.18 + 0.82 * rand() * env) * 100);
      bars.push(`<span style="--h:${h}%"></span>`);
    }
    const markup = bars.join('');
    // Two stacked layers: a dim base and a bright "played" copy clipped to progress.
    this.wave.innerHTML =
      `<span class="${LAYER} ${LAYER}--base">${markup}</span>` +
      `<span class="${LAYER} ${LAYER}--played">${markup}</span>`;
    if (REDUCED_MOTION.matches) this.wave.classList.add('is-reduced-motion');
  }

  #bind() {
    this.playBtn.addEventListener('click', () => this.togglePlay());
    this.toggle.addEventListener('click', () => this.setWet(!this.wet));
    this.wave.addEventListener('click', (e) => this.#seekFromPointer(e));
    this.wave.addEventListener('keydown', (e) => this.#seekFromKey(e));
  }

  get hasAudio() {
    return Boolean(this.dryUrl || this.wetUrl);
  }

  async #ensureLoaded() {
    if (this.buffers) return this.buffers;
    if (!this.loading) {
      this.el.classList.add('is-loading');
      const urls = [this.dryUrl, this.wetUrl];
      this.loading = Promise.all(urls.map((u) => (u ? loadBuffer(u) : Promise.resolve(null))))
        .then(([dry, wet]) => {
          this.buffers = { dry, wet };
          const durations = [dry, wet].filter(Boolean).map((b) => b.duration);
          this.loopLen = durations.length ? Math.min(...durations) : 0;
          this.el.classList.remove('is-loading');
          return this.buffers;
        })
        .catch((err) => {
          this.el.classList.remove('is-loading');
          this.el.classList.add('is-error');
          this.loading = null;
          throw err;
        });
    }
    return this.loading;
  }

  togglePlay() {
    if (this.playing) this.pause();
    else this.play();
  }

  async play() {
    if (!this.hasAudio || this.playing) return;
    const ctx = getContext();
    if (ctx.state === 'suspended') await ctx.resume();
    try {
      await this.#ensureLoaded();
    } catch (e) {
      return; // load or decode failed; the player shows is-error
    }
    if (this.playing) return; // guard against a double trigger while awaiting

    // Single playback: stop whatever was sounding first, here and elsewhere.
    if (currentPlayer && currentPlayer !== this) currentPlayer.stop();
    currentPlayer = this;
    announceStart(this.el);

    this.#startSources(this.offset);
    this.playing = true;
    this.#reflectPlaying();
    this.#startProgressLoop();
  }

  /** Build the dry/wet graph and start both from `offset`, gains set instantly (no dip). */
  #startSources(offset) {
    const ctx = getContext();
    const { dry, wet } = this.buffers;

    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();
    this.dryGain.connect(ctx.destination);
    this.wetGain.connect(ctx.destination);
    // Set the correct source instantly at (re)start: no crossfade, no dip, no wrong source.
    this.dryGain.gain.value = this.wet ? 0 : 1;
    this.wetGain.gain.value = this.wet ? 1 : 0;

    const start = ctx.currentTime;
    const safeOffset = this.loopLen ? offset % this.loopLen : 0;

    if (dry) {
      this.drySource = ctx.createBufferSource();
      this.drySource.buffer = dry;
      this.drySource.loop = true;
      this.drySource.loopStart = 0;
      this.drySource.loopEnd = this.loopLen;
      this.drySource.connect(this.dryGain);
      this.drySource.start(start, safeOffset);
    }
    if (wet) {
      this.wetSource = ctx.createBufferSource();
      this.wetSource.buffer = wet;
      this.wetSource.loop = true;
      this.wetSource.loopStart = 0;
      this.wetSource.loopEnd = this.loopLen;
      this.wetSource.connect(this.wetGain);
      this.wetSource.start(start, safeOffset);
    }

    this.startedAt = start;
    this.startOffset = safeOffset;
  }

  /** Tear down the running sources without touching play or offset state. */
  #stopSources() {
    for (const src of [this.drySource, this.wetSource]) {
      if (!src) continue;
      try {
        src.onended = null;
        src.stop();
        src.disconnect();
      } catch (e) {
        /* already stopped */
      }
    }
    this.drySource = this.wetSource = null;
    if (this.dryGain) this.dryGain.disconnect();
    if (this.wetGain) this.wetGain.disconnect();
    this.dryGain = this.wetGain = null;
  }

  #currentPosition() {
    if (!this.playing || !this.loopLen) return this.offset;
    const ctx = getContext();
    return (this.startOffset + (ctx.currentTime - this.startedAt)) % this.loopLen;
  }

  /** Pause: keep the playhead so play() resumes from here. */
  pause() {
    if (!this.playing) return;
    this.offset = this.#currentPosition();
    this.#stopSources();
    this.playing = false;
    this.#stopProgressLoop();
    this.#reflectPlaying();
    if (currentPlayer === this) currentPlayer = null;
  }

  /** Stop: full reset to the start (used by single playback and stop on close). */
  stop() {
    this.#stopSources();
    this.playing = false;
    this.offset = 0;
    this.#stopProgressLoop();
    this.#reflectPlaying();
    this.#paintProgress(0);
    if (currentPlayer === this) currentPlayer = null;
  }

  /** Dry/wet toggle. While playing, crossfade gains (both sources keep running). */
  setWet(wet) {
    this.wet = wet;
    this.toggle.setAttribute('aria-checked', String(wet));
    this.el.classList.toggle('is-wet', wet);
    if (this.playing && this.dryGain && this.wetGain) {
      const ctx = getContext();
      const t = ctx.currentTime;
      for (const [gain, target] of [
        [this.dryGain.gain, wet ? 0 : 1],
        [this.wetGain.gain, wet ? 1 : 0],
      ]) {
        gain.cancelScheduledValues(t);
        gain.setValueAtTime(gain.value, t);
        gain.linearRampToValueAtTime(target, t + CROSSFADE);
      }
    }
  }

  #seekFromPointer(e) {
    const rect = this.wave.getBoundingClientRect();
    if (!rect.width) return;
    const f = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    this.#seekTo(f);
  }

  #seekFromKey(e) {
    const step = 0.05;
    let f = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') f = this.#progressFraction() + step;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') f = this.#progressFraction() - step;
    else if (e.key === 'Home') f = 0;
    else if (e.key === 'End') f = 1;
    else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.togglePlay();
      return;
    } else return;
    e.preventDefault();
    this.#seekTo(Math.min(1, Math.max(0, f)));
  }

  #seekTo(fraction) {
    const len = this.loopLen || 0;
    const newOffset = len * fraction;
    this.offset = newOffset;
    if (this.playing) {
      // Recreate both sources at the new offset; gains are set instantly per the current
      // toggle inside #startSources, so the right source is heard immediately, no dip.
      this.#stopSources();
      this.#startSources(newOffset);
    }
    this.#paintProgress(fraction);
  }

  #progressFraction() {
    if (!this.loopLen) return 0;
    return this.#currentPosition() / this.loopLen;
  }

  #startProgressLoop() {
    this.#stopProgressLoop();
    const tick = () => {
      this.#paintProgress(this.#progressFraction());
      this.raf = window.requestAnimationFrame(tick);
    };
    this.raf = window.requestAnimationFrame(tick);
  }

  #stopProgressLoop() {
    if (this.raf) window.cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  #paintProgress(fraction) {
    // Reduced motion: step the fill coarsely (~5%) instead of sweeping every frame.
    let f = fraction;
    if (REDUCED_MOTION.matches) f = Math.round(fraction * 20) / 20;
    f = Math.min(1, Math.max(0, f));
    // The played layer of the waveform is clipped to this percentage.
    this.wave.style.setProperty('--p', f * 100 + '%');
    this.wave.setAttribute('aria-valuenow', String(Math.round(f * 100)));
  }

  #reflectPlaying() {
    this.el.classList.toggle('is-playing', this.playing);
    this.playBtn.setAttribute('aria-pressed', String(this.playing));
    this.playBtn.setAttribute('aria-label', this.playing ? this.pauseLabel : this.playLabel);
  }
}

/**
 * Tell the rest of the page that a player started: pause every other <audio> element (a
 * surface that does not use this module), then announce it for anything that listens.
 */
function announceStart(el) {
  for (const other of document.querySelectorAll('audio')) {
    if (!other.paused) other.pause();
  }
  announcing = true;
  document.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: { player: el } }));
  announcing = false;
}

// A foreign player started, so stand down. `announcing` skips our own echo.
document.addEventListener(PLAY_EVENT, () => {
  if (announcing) return;
  if (currentPlayer) currentPlayer.stop();
});

/** Enhance every not-yet-enhanced player on the page. Idempotent. */
function enhance() {
  const dialogs = new Map();
  for (const el of document.querySelectorAll(ROOT)) {
    if (el.dataset.enhanced) continue;
    if (!el.dataset.dry && !el.dataset.wet) continue; // nothing to play
    if (!el.querySelector(PLAY) || !el.querySelector(WAVE) || !el.querySelector(TOGGLE)) continue;
    el.dataset.enhanced = 'true';
    const player = new DryWetPlayer(el);

    const dialog = el.closest('dialog');
    if (dialog) {
      if (!dialogs.has(dialog)) dialogs.set(dialog, []);
      dialogs.get(dialog).push(player);
    }
  }

  // Stop on close: the native `close` event fires for every close path (button, Esc,
  // click outside) because dialog.js always calls dialog.close().
  dialogs.forEach((players, dialog) => {
    if (dialog.dataset.bnoStopOnClose) return;
    dialog.dataset.bnoStopOnClose = 'true';
    dialog.addEventListener('close', () => players.forEach((p) => p.stop()));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', enhance);
} else {
  enhance();
}

// Theme editor: a section re-render replaces the markup, so enhance the new copy.
document.addEventListener('shopify:section:load', enhance);
